import React from "react";
import { Row, Col, Form, Button, Badge } from "react-bootstrap";
import ImageInput from "./ImageInput";
import StatusSwitch from "./StatusSwitch";
import TextStylesEditor from "./TextStylesEditor";
import { WIDGET_SIZE_OPTIONS, ALIGNMENT_OPTIONS, VARIANT_SHAPE_OPTIONS } from "../../helpers/tmConstants";

/**
 * VariantFields — reusable editor for a SINGLE widget variant (size).
 * Used both by the add-page in-memory builder and the edit-page live
 * manager. Sizes already used by the widget are disabled to enforce
 * the "one variant per size" rule at the UI level (backend re-checks).
 *
 * Props:
 *  - value       : working variant object (fields + File uploads)
 *  - existing    : existing remote URLs { preview_image, widget_image, clock_image }
 *  - usedSizes   : sizes already taken by other variants (to disable)
 *  - errors      : field->message map
 *  - onField     : (field, value) => void
 *  - onFile      : (field, File|null) => void
 *  - onRemove    : () => void   (optional)
 *  - title       : header label
 *  - disabled    : disable every field + the Remove button (e.g. while saving)
 */
const VariantFields = ({
  value,
  existing = {},
  usedSizes = [],
  errors = {},
  onField,
  onFile,
  onRemove,
  title = "Variant",
  disabled = false,
  category = "",
}) => {
  return (
    <div className="border rounded-3 my-3 p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">
          {title}{" "}
          {value.widget_type && <Badge bg="info" className="text-capitalize">{value.widget_type}</Badge>}
        </h6>
        {onRemove && (
          <Button variant="outline-danger" size="sm" onClick={onRemove} disabled={disabled}>
            <i className="bx bx-trash-alt"></i> Remove
          </Button>
        )}
      </div>
      <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* Row 1 — selects that define the variant's identity. */}
      <Row>
        <Col lg={3} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Size</Form.Label>
            <Form.Select
              value={value.widget_type || ""}
              onChange={(e) => onField("widget_type", e.target.value)}
              isInvalid={!!errors.widget_type}
            >
              <option value="">Select Size</option>
              {WIDGET_SIZE_OPTIONS.map((o) => (
                <option
                  key={o.value}
                  value={o.value}
                  disabled={usedSizes.includes(o.value) && value.widget_type !== o.value}
                >
                  {o.label}{usedSizes.includes(o.value) && value.widget_type !== o.value ? " (used)" : ""}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.widget_type}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Alignment</Form.Label>
            <Form.Select value={value.alignment || ""} onChange={(e) => onField("alignment", e.target.value)}>
              <option value="">Default</option>
              {ALIGNMENT_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col lg={2} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Shape</Form.Label>
            <Form.Select value={value.shape || ""} onChange={(e) => onField("shape", e.target.value)}>
              <option value="">Default</option>
              {VARIANT_SHAPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col lg={2} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Content Padding</Form.Label>
            <Form.Control
              value={value.content_padding || ""}
              onChange={(e) => onField("content_padding", e.target.value || null)}
              placeholder="e.g. 8 12 8 12"
              isInvalid={!!errors.content_padding}
            />
            <Form.Control.Feedback type="invalid">{errors.content_padding}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={2} md={6} sm={6}>
          <Form.Label className="d-block">Active</Form.Label>
          <StatusSwitch checked={value.status ?? true} disabled={disabled} onChange={(checked) => onField("status", checked)} />
        </Col>
      </Row>

      {/* Row 2 — free-text / color styling. */}
      <Row className="mt-2">
        <Col lg={4} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Font Name</Form.Label>
            <Form.Control value={value.font_name || ""} onChange={(e) => onField("font_name", e.target.value)} placeholder="e.g. Roboto" />
          </Form.Group>
        </Col>
        <Col lg={2} md={6} sm={6}>
          <Form.Group>
            <Form.Label>Text Color</Form.Label>
            <Form.Control type="color" value={value.text_color || "#000000"} onChange={(e) => onField("text_color", e.target.value)} title="Pick a color" />
          </Form.Group>
        </Col>
        <Col lg={6} md={12} sm={12}>
          <Form.Group>
            <Form.Label>Quote</Form.Label>
            <Form.Control as="textarea" rows={1} value={value.quote || ""} onChange={(e) => onField("quote", e.target.value)} placeholder="Optional quote text" />
          </Form.Group>
        </Col>
      </Row>

      {/* Row 3 — assets. */}
      <Row className="mt-2">
        <Col lg={4} sm={6}>
          <ImageInput label="Preview Image *" existingUrl={existing.preview_image} onChange={(f) => onFile("preview_image", f)} error={errors.preview_image} disabled={disabled} />
        </Col>
        <Col lg={4} sm={6}>
          <ImageInput label="Widget Image *" existingUrl={existing.widget_image} onChange={(f) => onFile("widget_image", f)} error={errors.widget_image} disabled={disabled} />
        </Col>
        <Col lg={4} sm={6}>
          <ImageInput label="Clock Image" existingUrl={existing.clock_image} onChange={(f) => onFile("clock_image", f)} error={errors.clock_image} disabled={disabled} />
        </Col>
      </Row>

      {/* ── Configuration Parameters ─────────────────────────────────── */}
      <hr className="mt-3 mb-2" />
      <h6 className="text-muted mb-2" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Configuration Parameters
      </h6>
      <Row className="mt-2">
        <Col lg={2} md={4} sm={6}>
          <Form.Group>
            <Form.Label>Accent Color</Form.Label>
            <Form.Control type="color" value={value.accent_color || "#000000"} onChange={(e) => onField("accent_color", e.target.value)} title="Accent color (rings, bars, highlights)" isInvalid={!!errors.accent_color} />
            {value.accent_color && <Button variant="link" size="sm" className="p-0 mt-1 text-danger" onClick={() => onField("accent_color", null)}>Clear</Button>}
            <Form.Control.Feedback type="invalid">{errors.accent_color}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Form.Group>
            <Form.Label>Bolt Color</Form.Label>
            <Form.Control type="color" value={value.bolt_color || "#000000"} onChange={(e) => onField("bolt_color", e.target.value)} title="Charging bolt tint" isInvalid={!!errors.bolt_color} />
            {value.bolt_color && <Button variant="link" size="sm" className="p-0 mt-1 text-danger" onClick={() => onField("bolt_color", null)}>Clear</Button>}
            <Form.Control.Feedback type="invalid">{errors.bolt_color}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Analog-clock specific fields — only visible for Analog category */}
      {category === "Analog" && (
        <Row className="mt-2">
          <Col lg={4} sm={6}>
            <ImageInput label="Hour Hand Image" existingUrl={existing.hour_hand_image} onChange={(f) => onFile("hour_hand_image", f)} error={errors.hour_hand_image} disabled={disabled} />
          </Col>
          <Col lg={4} sm={6}>
            <ImageInput label="Minute Hand Image" existingUrl={existing.minute_hand_image} onChange={(f) => onFile("minute_hand_image", f)} error={errors.minute_hand_image} disabled={disabled} />
          </Col>
          <Col lg={2} sm={6}>
            <Form.Group>
              <Form.Label>Hand Center X <small className="text-muted">(0–1)</small></Form.Label>
              <Form.Control
                type="number" min={0} max={1} step={0.01}
                value={value.hand_center_x ?? ""}
                onChange={(e) => onField("hand_center_x", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="0.5"
                isInvalid={!!errors.hand_center_x}
                disabled={disabled}
              />
              <Form.Control.Feedback type="invalid">{errors.hand_center_x}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={2} sm={6}>
            <Form.Group>
              <Form.Label>Hand Center Y <small className="text-muted">(0–1)</small></Form.Label>
              <Form.Control
                type="number" min={0} max={1} step={0.01}
                value={value.hand_center_y ?? ""}
                onChange={(e) => onField("hand_center_y", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="0.5"
                isInvalid={!!errors.hand_center_y}
                disabled={disabled}
              />
              <Form.Control.Feedback type="invalid">{errors.hand_center_y}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Per-role typography (Text Styles) — not for Analog */}
      {category !== "Analog" && (
        <TextStylesEditor
          value={value.text_styles || []}
          category={category}
          disabled={disabled}
          onChange={(arr) => onField("text_styles", arr)}
        />
      )}

      </fieldset>
    </div>
  );
};

export default VariantFields;
